import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/Card";
import { useToast } from "../hooks/useToast";
import { db } from "../lib/firebase";
import { collection, query, where, getDocs, addDoc, serverTimestamp, orderBy } from "firebase/firestore";
import { HUBSTOPS, formatCurrency } from "../lib/utils";
import { Footer } from "../components/Footer";

interface PublicRoute {
  id: string;
  name: string;
  description?: string;
  hubstopId?: string;
  hubstopName?: string;
  destinationName: string;
  destinationAddress: string;
  pickupZipcodes: string[];
  pricePerWeek: number;
  spotsAvailable: number;
  totalSpots: number;
  schedule: string;
  createdAt: Date;
}

export default function Routes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [routes, setRoutes] = useState<PublicRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchZipcode, setSearchZipcode] = useState("");
  const [filteredRoutes, setFilteredRoutes] = useState<PublicRoute[]>([]);
  const [joiningRouteId, setJoiningRouteId] = useState<string | null>(null);

  // Load public routes
  useEffect(() => {
    async function loadRoutes() {
      try {
        const routesQuery = query(
          collection(db, "publicRoutes"),
          where("isActive", "==", true),
          orderBy("createdAt", "desc")
        );
        const snapshot = await getDocs(routesQuery);
        const loadedRoutes: PublicRoute[] = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
        })) as PublicRoute[];

        // If no routes in database, show sample hubstop routes
        const routesToDisplay = loadedRoutes.length > 0 ? loadedRoutes : getSampleRoutes();
        setRoutes(routesToDisplay);
        setFilteredRoutes(routesToDisplay);
      } catch (error) {
        console.error("Error loading routes:", error);
        // Load sample routes for demo
        setRoutes(getSampleRoutes());
        setFilteredRoutes(getSampleRoutes());
      } finally {
        setLoading(false);
      }
    }

    loadRoutes();
  }, []);

  // Filter routes by zipcode
  useEffect(() => {
    if (!searchZipcode) {
      setFilteredRoutes(routes);
      return;
    }

    const filtered = routes.filter((route) =>
      route.pickupZipcodes.some((zip) => zip.startsWith(searchZipcode))
    );
    setFilteredRoutes(filtered);
  }, [searchZipcode, routes]);

  // Join route interest
  const handleJoinRoute = async (route: PublicRoute) => {
    if (!user) {
      navigate(`/auth?redirect=/routes`);
      return;
    }

    setJoiningRouteId(route.id);

    try {
      // Add inquiry to database
      await addDoc(collection(db, "routeInterests"), {
        routeId: route.id,
        routeName: route.name,
        userId: user.uid,
        userEmail: user.email,
        zipcode: searchZipcode || "not specified",
        createdAt: serverTimestamp(),
        status: "pending",
      });

      // Send email notification via Zapier webhook
      const webhookData = {
        name: user.displayName || "Unknown",
        email: user.email,
        phone: user.phoneNumber || "Not provided",
        message: `User expressed interest in joining route: ${route.name} (${route.id}). Zipcode: ${searchZipcode || "not specified"}`,
        inquiryType: "quote",
        targetEmail: "hello@getshuttlebee.com",
        subject: "Route Interest - Join Route Request",
        timestamp: new Date().toISOString(),
        routeId: route.id,
        routeName: route.name,
        zipcode: searchZipcode || "not specified",
      };

      try {
        await fetch("https://hooks.zapier.com/hooks/catch/22972432/uwg1rad/", {
          method: "POST",
          body: JSON.stringify(webhookData),
        });
      } catch (emailError) {
        console.error("Error sending email notification:", emailError);
        // Continue even if email fails
      }

      toast({
        type: "success",
        title: "Interest registered!",
        description: "Redirecting you to your dashboard...",
      });

      // Redirect to dashboard route interests section after a short delay
      setTimeout(() => {
        navigate("/dashboard", { state: { fromRouteInterest: true } });
      }, 2000);
    } catch (error) {
      console.error("Error joining route:", error);
      toast({
        type: "error",
        title: "Failed to join",
        description: "Please try again or contact support.",
      });
      setJoiningRouteId(null);
    }
  };

  // Sample routes for demo
  function getSampleRoutes(): PublicRoute[] {
    return HUBSTOPS.map((hubstop, index) => ({
      id: `sample-${hubstop.id}`,
      name: `${hubstop.name} → Summerside Camps at SCH`,
      hubstopId: hubstop.id,
      hubstopName: hubstop.name,
      destinationName: "Springside Chestnut Hill Academy",
      destinationAddress: "500 W Willow Grove Ave, Philadelphia, PA 19118",
      pickupZipcodes: hubstop.zipCodes,
      pricePerWeek: hubstop.pricePerWeek,
      spotsAvailable: Math.floor(Math.random() * 4) + 2,
      totalSpots: 6,
      schedule: `M-F, ${hubstop.meetingTime} Pickup, ${(hubstop as any).departureTime} Departure`,
      createdAt: new Date(),
      description: `Direct route from ${hubstop.name} neighborhood to Summerside Camps at SCH`,
    }));
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-shuttlebee-yellow"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-shuttlebee-dark via-shuttlebee-navy to-shuttlebee-blue text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-4 py-1 bg-shuttlebee-yellow/20 rounded-full text-shuttlebee-yellow text-sm font-medium mb-4">
            🔍 Route Discovery
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Find Your Route
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
            Browse available routes in your neighborhood or express interest in new ones. 
            Join other families to unlock group rates!
          </p>

          {/* Search Box */}
          <div className="max-w-md mx-auto">
            <div className="relative">
              <Input
                type="text"
                placeholder="Enter your zipcode (e.g., 19103)"
                value={searchZipcode}
                onChange={(e) => setSearchZipcode(e.target.value.replace(/\D/g, "").slice(0, 5))}
                className="pl-12 h-14 text-lg bg-white text-gray-900"
              />
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl">📍</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Results Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {searchZipcode
                ? `Routes near ${searchZipcode}`
                : "All Available Routes"}
            </h2>
            <p className="text-gray-600 mt-1">
              {filteredRoutes.length} route{filteredRoutes.length !== 1 ? "s" : ""} found
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate("/custom-quote")}>
            Request Custom Route
          </Button>
        </div>

        {/* Routes Grid */}
        {filteredRoutes.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRoutes.map((route) => (
              <Card key={route.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{route.name}</CardTitle>
                      <CardDescription className="mt-1">
                        {route.destinationName}
                      </CardDescription>
                    </div>
                    <span className="text-2xl">🚐</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Schedule */}
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span>🕐</span>
                      <span>{route.schedule}</span>
                    </div>

                    {/* Spots */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-shuttlebee-yellow h-2 rounded-full"
                          style={{
                            width: `${((route.totalSpots - route.spotsAvailable) / route.totalSpots) * 100}%`,
                          }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {route.spotsAvailable}/{route.totalSpots} spots
                      </span>
                    </div>

                    {/* Zipcodes */}
                    <div className="flex flex-wrap gap-1">
                      {route.pickupZipcodes.map((zip) => (
                        <span
                          key={zip}
                          className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600"
                        >
                          {zip}
                        </span>
                      ))}
                    </div>

                    {/* Actions */}
                    <div className="pt-4 border-t">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/summer-camp-quote?hubstop=${route.hubstopId}`)}
                          className="flex-1"
                        >
                          Get Quote
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleJoinRoute(route)}
                          loading={joiningRouteId === route.id}
                          disabled={route.spotsAvailable === 0}
                          className="flex-1"
                        >
                          {route.spotsAvailable === 0 ? "Waitlist" : "Join"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="text-center py-16">
            <CardContent>
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No routes found for {searchZipcode}
              </h3>
              <p className="text-gray-600 mb-6">
                We don't have an active route in your area yet, but you can request one!
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => setSearchZipcode("")} variant="outline">
                  Clear Search
                </Button>
                <Button onClick={() => navigate("/custom-quote")}>
                  Request Custom Route
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* How It Works Section */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-12">
            How Route Discovery Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-shuttlebee-yellow/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🔍</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">1. Search</h3>
              <p className="text-gray-600">
                Enter your zipcode to find routes in your neighborhood or browse all available options.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-shuttlebee-yellow/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">👥</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">2. Join</h3>
              <p className="text-gray-600">
                Express interest in a route. When enough families join, we activate the route with group pricing.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-shuttlebee-yellow/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🚐</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">3. Ride</h3>
              <p className="text-gray-600">
                Once confirmed, you'll be matched with a vetted driver and receive real-time tracking.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-shuttlebee-dark to-shuttlebee-navy rounded-2xl p-8 md:p-12 text-center text-white">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Don't see your route?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Request a custom route and we'll work to match you with nearby families 
            to create a new route in your area.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="shuttlebee"
              size="lg"
              onClick={() => navigate("/custom-quote")}
            >
              Request Custom Route
            </Button>
            <Button
              size="lg"
              onClick={() => navigate("/contact")}
              className="bg-white text-shuttlebee-dark hover:bg-gray-100 shadow-lg"
            >
              Reach Out
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
